import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, useAuth } from "@/hooks/useAuth";
import KalkiGlobalHeader from "@/components/kalki/KalkiGlobalHeader";

/**
 * KALKI Temple — a quiet space for devotion, reflection and inner strength.
 *
 * Personalised landing greets the user with their current MD / AD + the
 * active Saturn phase, surfaced from Krishna Companion (Astro Monish v1.1).
 * Four functional cards reuse existing backend features:
 *
 *   • Ask Krishna  → navigates to /app/chat with a Temple-primed prompt.
 *   • My Remedies  → GET /api/remedies and renders active commitments.
 *   • Offer Prayer → POST /api/prayers (private)
 *   • Seva (Serve) → POST /api/kalki/karma/steps (creates a Seva Karma)
 *
 * The remaining four cards (Darshan · Mantra · Meditation · Aarti) ship as
 * real cards with a "coming soon" badge — no decorative buttons.
 */

const COMING_SOON = ["darshan", "mantra", "meditation", "aarti"];

export default function Temple() {
  const nav = useNavigate();
  const { user } = useAuth() || {};
  const [companion, setCompanion] = useState(null);
  const [prayerOpen, setPrayerOpen] = useState(false);
  const [sevaOpen, setSevaOpen] = useState(false);
  const [remediesOpen, setRemediesOpen] = useState(false);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const { data } = await api.get("/kalki/companion", {
          params: {topic: "spiritual"}});
        if (!dead) setCompanion(data?.companion || null);
      } catch (_) {}
    })();
    return () => { dead = true; };
  }, []);

  // Astro-powered greeting line. All planet/date claims come from the
  // Monish engine via companion.astrology — the LLM never computes.
  const greeting = () => {
    const name = user?.name?.split(" ")[0] || "friend";
    const astro = companion?.astrology || {};
    const dash = astro?.dasha_now?.value || astro?.dasha_now || {};
    const md = dash?.mahadasha?.lord;
    const ad = dash?.antardasha?.lord;
    const phases = (astro?.saturn_phases?.value || astro?.saturn_phases || []);
    const currentPhase = Array.isArray(phases)
      ? phases.find((p) => (p?.phase || "").toLowerCase().includes("sade sati")
                           || (p?.phase || "").toLowerCase().includes("ashtama")
                           || (p?.phase || "").toLowerCase().includes("kantaka"))
      : null;
    if (md && ad) {
      return `Welcome, ${name}. You are walking the ${md} major period with ${ad} sub-period${currentPhase ? ` under ${currentPhase.phase}` : ""}. A moment of stillness here can bring clarity.`;
    }
    return `Welcome, ${name}. A moment of stillness here can bring clarity.`;
  };

  const askKrishna = () => {
    const prompt = encodeURIComponent(
      "I'm in the Temple right now. Guide me on one meaningful practice I can do today."
    );
    nav(`/app/chat?prompt=${prompt}&source=temple`);
  };

  return (
    <div data-testid="temple-page" style={{minHeight: "100vh",
          background: "linear-gradient(180deg,#F8F3EC 0%,#FFFFFF 60%)"}}>
      <KalkiGlobalHeader />
      <main style={{maxWidth: 960, margin: "0 auto",
                     padding: "40px 20px 80px"}}>
        <div style={{textAlign: "center", marginBottom: 36}}>
          <div style={{fontSize: 10, letterSpacing: "0.32em",
                        color: "#5D00FF", marginBottom: 8}}>KALKI TEMPLE</div>
          <h1 style={{fontSize: 36, fontWeight: 400, letterSpacing: "-0.01em",
                       color: "#1C1C1C", margin: "0 0 10px 0"}}>
            Your quiet space for devotion.
          </h1>
          <p data-testid="temple-greeting"
             style={{fontSize: 14.5, color: "#434353", margin: 0,
                      lineHeight: 1.6, maxWidth: 620, marginInline: "auto"}}>
            {greeting()}
          </p>
        </div>

        <div style={{display: "grid", gap: 16,
                      gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))"}}>
          <TempleCard id="ask-krishna" title="Ask KALKI"
                      body="A guided conversation that already knows your chart and your path."
                      cta="Ask KALKI" onClick={askKrishna} />
          <TempleCard id="remedies" title="My Remedies"
                      body="Open the practices you have committed to this phase."
                      cta="View Remedies" onClick={() => setRemediesOpen(true)} />
          <TempleCard id="prayer" title="Offer Prayer"
                      body="A private intention — only you can see what you write here."
                      cta="Offer Prayer" onClick={() => setPrayerOpen(true)} />
          <TempleCard id="seva" title="Seva"
                      body="Turn devotion into one small act of service this week."
                      cta="Create Seva Karma" onClick={() => setSevaOpen(true)} />
          {COMING_SOON.map((id) => (
            <TempleCard key={id} id={id}
                        title={({darshan: "Daily Darshan", mantra: "Mantra",
                                 meditation: "Meditation", aarti: "Aarti"})[id]}
                        body="Coming soon — KALKI is still composing this experience."
                        cta="" soon />
          ))}
        </div>
      </main>

      {prayerOpen && <PrayerSheet onClose={() => setPrayerOpen(false)} />}
      {sevaOpen && <SevaSheet onClose={() => setSevaOpen(false)} />}
      {remediesOpen && <RemediesSheet onClose={() => setRemediesOpen(false)} />}
    </div>
  );
}

function TempleCard({ id, title, body, cta, onClick, soon }) {
  return (
    <div data-testid={`temple-card-${id}`}
         style={{background: "white", borderRadius: 20, padding: 22,
                  border: "1px solid rgba(0,0,0,0.05)",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.03)"}}>
      <div style={{display: "flex", justifyContent: "space-between",
                    alignItems: "center", marginBottom: 10}}>
        <div style={{fontSize: 10, letterSpacing: "0.3em",
                      color: "#9999A5"}}>{title.toUpperCase()}</div>
        {soon && (
          <div style={{fontSize: 9, letterSpacing: "0.2em",
                        color: "#FF2E9A"}}>SOON</div>
        )}
      </div>
      <div style={{fontSize: 18, color: "#1C1C1C", fontWeight: 500,
                    marginBottom: 8, fontFamily: "'EB Garamond',serif"}}>
        {title}
      </div>
      <p style={{fontSize: 13, color: "#676773", lineHeight: 1.6,
                  margin: "0 0 18px 0", minHeight: 50}}>{body}</p>
      {cta && !soon && (
        <button onClick={onClick}
                data-testid={`temple-cta-${id}`}
                style={{background: "linear-gradient(135deg,#5D00FF,#FF2E9A)",
                         color: "white", border: 0,
                         padding: "11px 18px", borderRadius: 999,
                         fontSize: 12, letterSpacing: "0.14em",
                         cursor: "pointer", fontWeight: 500}}>
          {cta}
        </button>
      )}
    </div>
  );
}

function Sheet({ title, children, onClose, testid }) {
  return (
    <div data-testid={testid}
         onClick={onClose}
         style={{position: "fixed", inset: 0, zIndex: 100,
                  background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)",
                  display: "flex", alignItems: "center",
                  justifyContent: "center", padding: 20}}>
      <div onClick={(e) => e.stopPropagation()}
           style={{background: "white", borderRadius: 24, padding: 28,
                    maxWidth: 480, width: "100%",
                    boxShadow: "0 24px 60px rgba(0,0,0,0.3)"}}>
        <div style={{display: "flex", justifyContent: "space-between",
                      alignItems: "center", marginBottom: 16}}>
          <h3 style={{margin: 0, fontSize: 20, fontWeight: 500,
                       color: "#1C1C1C",
                       fontFamily: "'EB Garamond',serif"}}>{title}</h3>
          <button onClick={onClose}
                  data-testid={`${testid}-close`}
                  style={{background: "none", border: 0, fontSize: 22,
                           color: "#9999A5", cursor: "pointer"}}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function PrayerSheet({ onClose }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post("/prayers", {text: text.trim(), source: "temple"});
      toast.success("Your prayer is kept privately.");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save prayer.");
    } finally { setBusy(false); }
  };
  return (
    <Sheet title="Offer Prayer" onClose={onClose} testid="temple-prayer-sheet">
      <p style={{fontSize: 13, color: "#676773", marginTop: 0,
                  marginBottom: 14, lineHeight: 1.6}}>
        Write freely. This stays private — only you can see what you write here.
      </p>
      <textarea data-testid="temple-prayer-input" value={text}
                onChange={(e) => setText(e.target.value)}
                rows={5} placeholder="KALKI, I pray for…"
                style={{width: "100%", borderRadius: 12,
                         border: "1px solid rgba(0,0,0,0.08)",
                         padding: 14, fontSize: 14, color: "#1C1C1C",
                         resize: "vertical", fontFamily: "inherit"}} />
      <button onClick={submit} disabled={busy || !text.trim()}
              data-testid="temple-prayer-submit"
              style={{marginTop: 14, width: "100%",
                       background: "linear-gradient(135deg,#5D00FF,#FF2E9A)",
                       color: "white", border: 0, padding: "13px 18px",
                       borderRadius: 999, fontSize: 12,
                       letterSpacing: "0.18em", cursor: "pointer",
                       opacity: busy || !text.trim() ? 0.5 : 1}}>
        {busy ? "Offering…" : "Offer Prayer"}
      </button>
    </Sheet>
  );
}

function SevaSheet({ onClose }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post("/kalki/karma/steps", {
        text: text.trim(), category: "seva", due_in_days: 7,
      });
      toast.success("Seva Karma added. KALKI will remind you.");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add seva.");
    } finally { setBusy(false); }
  };
  return (
    <Sheet title="Seva · act of service" onClose={onClose} testid="temple-seva-sheet">
      <p style={{fontSize: 13, color: "#676773", marginTop: 0,
                  marginBottom: 14, lineHeight: 1.6}}>
        One small, specific act you'll do this week — help someone, offer time, teach a skill.
      </p>
      <input data-testid="temple-seva-input" value={text}
             onChange={(e) => setText(e.target.value)}
             placeholder="Help my neighbour with grocery delivery"
             style={{width: "100%", borderRadius: 999,
                      border: "1px solid rgba(0,0,0,0.08)",
                      padding: "14px 18px", fontSize: 14, color: "#1C1C1C"}} />
      <button onClick={submit} disabled={busy || !text.trim()}
              data-testid="temple-seva-submit"
              style={{marginTop: 14, width: "100%",
                       background: "linear-gradient(135deg,#5D00FF,#FF2E9A)",
                       color: "white", border: 0, padding: "13px 18px",
                       borderRadius: 999, fontSize: 12,
                       letterSpacing: "0.18em", cursor: "pointer",
                       opacity: busy || !text.trim() ? 0.5 : 1}}>
        {busy ? "Creating…" : "Create Seva Karma"}
      </button>
    </Sheet>
  );
}

function RemediesSheet({ onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/remedies");
            setItems(data.items || []); }
      catch (_) { setItems([]); }
    })();
  }, []);
  const markDone = async (id) => {
    try { await api.post(`/remedies/${id}/done`);
          setItems((x) => x.map((r) => r.id === id ? {...r, status: "done"} : r));
          toast.success("Marked done."); }
    catch (_) {}
  };
  const active = (items || []).filter((r) => r.status === "active");
  return (
    <Sheet title="My Remedies" onClose={onClose} testid="temple-remedies-sheet">
      {items === null && <p style={{color: "#9999A5"}}>Loading…</p>}
      {items && active.length === 0 && (
        <p data-testid="temple-remedies-empty"
           style={{fontSize: 13.5, color: "#676773", lineHeight: 1.6}}>
          You have no active remedies yet. Open Chat and ask KALKI for one
          — tap <strong>Start Remedy</strong> in his reply and it will appear here.
        </p>
      )}
      {active.map((r) => (
        <div key={r.id} data-testid={`temple-remedy-${r.id}`}
             style={{padding: "12px 14px", borderRadius: 14,
                      border: "1px solid rgba(0,0,0,0.06)",
                      marginBottom: 8, display: "flex",
                      alignItems: "flex-start", gap: 12}}>
          <div style={{flex: 1, minWidth: 0}}>
            <div style={{fontSize: 14, color: "#1C1C1C"}}>{r.remedy_text || r.text}</div>
            <div style={{fontSize: 10, letterSpacing: "0.15em",
                          color: "#9999A5", marginTop: 2,
                          textTransform: "uppercase"}}>
              {r.planet ? `${r.planet} · ` : ""}{r.category || "general"}
            </div>
          </div>
          <button onClick={() => markDone(r.id)}
                  data-testid={`temple-remedy-done-${r.id}`}
                  style={{background: "none", border: 0,
                           fontSize: 11, letterSpacing: "0.14em",
                           color: "#5D00FF", cursor: "pointer"}}>
            DONE
          </button>
        </div>
      ))}
    </Sheet>
  );
}
